import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  AddShoppingCart as AddCartIcon,
  EmojiObjects as SuggestionsIcon,
  LocalPharmacy as PharmacyIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  ShoppingCart as CartIcon,
  Warning as WarningIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 0,
  },
  introBox: {
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    boxShadow: theme.shadows[2],
  },
  introIcon: {
    fontSize: 28,
    color: "inherit",
  },
  introTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "inherit",
  },
  introSubtitle: {
    fontSize: "0.75rem",
    opacity: 0.9,
    color: "inherit",
  },
  suggestionCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    objectFit: "cover",
    backgroundColor: theme.palette.grey[200],
  },
  productImagePlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[400],
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontWeight: 600,
    fontSize: "0.9rem",
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginBottom: theme.spacing(0.5),
  },
  productBarcode: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  priceContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  price: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.primary.main,
  },
  scoreChip: {
    height: 24,
    fontSize: "0.7rem",
    fontWeight: 600,
  },
  highScore: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  mediumScore: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  lowScore: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
  },
  reasonBox: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 4,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  reasonText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  statsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  cardActions: {
    marginTop: "auto",
    padding: theme.spacing(1, 2, 2),
  },
  addButton: {
    flex: 1,
  },
  stockWarning: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.75rem",
    color: theme.palette.warning.dark,
    marginTop: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  categoryChip: {
    fontSize: "0.7rem",
    height: 20,
    marginTop: 4,
  },
}));

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const getScoreClass = (score, classes) => {
  if (score >= 70) return classes.highScore;
  if (score >= 40) return classes.mediumScore;
  return classes.lowScore;
};

const getReasonIcon = (reason) => {
  if (reason.includes("frequente") || reason.includes("comprou")) {
    return <CartIcon fontSize="small" style={{ fontSize: 14 }} />;
  }
  if (reason.includes("dias") || reason.includes("tempo")) {
    return <ScheduleIcon fontSize="small" style={{ fontSize: 14 }} />;
  }
  return <TrendingIcon fontSize="small" style={{ fontSize: 14 }} />;
};

const SuggestionsTab = ({ suggestions, onAddToCart, storeId }) => {
  const classes = useStyles();

  if (!suggestions || !suggestions.suggestions || suggestions.suggestions.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <SuggestionsIcon style={{ fontSize: 48, opacity: 0.5 }} />
        <Typography variant="body1">Nenhuma sugestao disponivel</Typography>
        <Typography variant="body2" color="textSecondary">
          As sugestoes aparecerao com base no historico de compras do cliente
        </Typography>
      </Box>
    );
  }

  const handleAddToCart = (suggestion) => {
    if (onAddToCart) {
      onAddToCart({
        id: suggestion.id,
        name: suggestion.name,
        barcode: suggestion.barcode,
        imageUrl: suggestion.imageUrl,
        price: suggestion.price,
      });
    }
  };

  return (
    <Box className={classes.container}>
      {/* Intro Box */}
      <Box className={classes.introBox}>
        <SuggestionsIcon className={classes.introIcon} />
        <Box>
          <Typography className={classes.introTitle}>
            Sugestoes Inteligentes
          </Typography>
          <Typography className={classes.introSubtitle}>
            Produtos sugeridos com base no historico de compras.
            {suggestions.totalAnalyzed > 0 && (
              <> {suggestions.totalAnalyzed} produtos analisados.</>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Suggestions Grid */}
      <Grid container spacing={2}>
        {suggestions.suggestions.map((suggestion) => (
          <Grid item xs={12} sm={6} md={4} key={suggestion.id}>
            <Card className={classes.suggestionCard} variant="outlined">
              <CardContent>
                <Box className={classes.cardHeader}>
                  {suggestion.imageUrl ? (
                    <img
                      src={suggestion.imageUrl}
                      alt={suggestion.name}
                      className={classes.productImage}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <Box className={classes.productImagePlaceholder}>
                      <PharmacyIcon />
                    </Box>
                  )}
                  <Box className={classes.productInfo}>
                    <Tooltip title={suggestion.name}>
                      <Typography className={classes.productName}>
                        {suggestion.name}
                      </Typography>
                    </Tooltip>
                    {suggestion.barcode && (
                      <Typography className={classes.productBarcode}>
                        {suggestion.barcode}
                      </Typography>
                    )}
                    {suggestion.categoryName && (
                      <Chip
                        size="small"
                        label={suggestion.categoryName}
                        className={classes.categoryChip}
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Chip
                    size="small"
                    label={`${suggestion.score}%`}
                    className={`${classes.scoreChip} ${getScoreClass(suggestion.score, classes)}`}
                  />
                </Box>

                <Box className={classes.priceContainer}>
                  <Typography className={classes.price}>
                    {formatCurrency(suggestion.price)}
                  </Typography>
                </Box>

                {/* Reason Box */}
                <Box className={classes.reasonBox}>
                  <Typography className={classes.reasonText}>
                    {getReasonIcon(suggestion.reason)}
                    {suggestion.reason}
                  </Typography>
                </Box>

                {/* Stats */}
                <Box className={classes.statsRow}>
                  {suggestion.timesPurchased > 0 && (
                    <Box className={classes.statItem}>
                      <CartIcon fontSize="small" style={{ fontSize: 14 }} />
                      {suggestion.timesPurchased}x comprado
                    </Box>
                  )}
                  {suggestion.daysSinceLastPurchase !== undefined && (
                    <Box className={classes.statItem}>
                      <ScheduleIcon fontSize="small" style={{ fontSize: 14 }} />
                      {suggestion.daysSinceLastPurchase === 0
                        ? "Hoje"
                        : suggestion.daysSinceLastPurchase === 1
                        ? "Ontem"
                        : `${suggestion.daysSinceLastPurchase} dias`}
                    </Box>
                  )}
                </Box>

                {/* Stock Warning */}
                {suggestion.stockQuantity !== undefined && suggestion.stockQuantity <= 5 && (
                  <Box className={classes.stockWarning}>
                    <WarningIcon fontSize="small" style={{ fontSize: 14 }} />
                    {suggestion.stockQuantity === 0
                      ? "Sem estoque"
                      : `Apenas ${suggestion.stockQuantity} em estoque`}
                  </Box>
                )}
              </CardContent>

              <CardActions className={classes.cardActions}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  className={classes.addButton}
                  startIcon={<AddCartIcon />}
                  onClick={() => handleAddToCart(suggestion)}
                  disabled={suggestion.stockQuantity === 0}
                >
                  Adicionar
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SuggestionsTab;
